import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AccountSettingsResponse, getAccountSettings, UpsertAccountSettingsPayload, upsertAccountSettings } from "@/api/accountSettings";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";

export const accountSettingsQueryKey = (applianceId: string): readonly string[] => ["accountSettings", applianceId];

const accountSettingsIdleQueryKey = ["accountSettings", "__no_appliance__"] as const;

/** Fetches account settings for the active appliance so UI can read {@link useAccountSettingsUse24Hour} without opening My Account Settings. */
export function useAccountSettingsBootstrapQuery(applianceId: string): ReturnType<typeof useQuery<AccountSettingsResponse, Error>> {
	return useQuery<AccountSettingsResponse, Error>({
		queryKey: accountSettingsQueryKey(applianceId),
		queryFn: () => getAccountSettings(applianceId),
		enabled: Boolean(applianceId),
		retry: false,
	});
}

export function useAccountSettingsQuery(isOpen: boolean, applianceId: string): ReturnType<typeof useQuery<AccountSettingsResponse, Error>> {
	return useQuery<AccountSettingsResponse, Error>({
		queryKey: accountSettingsQueryKey(applianceId),
		queryFn: () => getAccountSettings(applianceId),
		enabled: isOpen && Boolean(applianceId),
		retry: false,
	});
}

export function useUpsertAccountSettingsMutation(
	applianceId: string
): ReturnType<typeof useMutation<AccountSettingsResponse, Error, UpsertAccountSettingsPayload>> {
	const queryClient = useQueryClient();

	return useMutation<AccountSettingsResponse, Error, UpsertAccountSettingsPayload>({
		mutationFn: (body: UpsertAccountSettingsPayload) => upsertAccountSettings(applianceId, body),
		onSuccess: (data) => {
			queryClient.setQueryData<AccountSettingsResponse>(accountSettingsQueryKey(applianceId), data);
		},
	});
}

/** Reads `use24Hour` from cached account settings for the active appliance (default `false` until loaded). */
export function useAccountSettingsUse24Hour(): boolean {
	const applianceId = useUserSessionStore((s) => s.active_device) as string | undefined;
	const hasAppliance = Boolean(applianceId);

	const { data } = useQuery<AccountSettingsResponse, Error, boolean>({
		queryKey: hasAppliance ? accountSettingsQueryKey(applianceId as string) : accountSettingsIdleQueryKey,
		queryFn: () => getAccountSettings(applianceId as string),
		enabled: hasAppliance,
		retry: false,
		select: (response) => response.use24Hour,
	});

	return data ?? false;
}
